import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useHolidays, useMe } from "@/api/queries";
import { useSession } from "@/api/session";
import { AppShell } from "@/app/app-shell";
import { homePathFor } from "@/app/nav";
import { Icon } from "@/components/ui/icon";

import { LoginPage } from "@/pages/auth/login";
import { OnboardingPage } from "@/pages/auth/onboarding";
import { ResetPasswordPage } from "@/pages/auth/reset-password";
import { AccountSettingsPage } from "@/pages/account-settings";

import { HrOverviewPage } from "@/pages/hr/overview";
import { HrAttendancePage } from "@/pages/hr/attendance";
import { HrLeavePage } from "@/pages/hr/leave";
import { HrAnalyticsPage } from "@/pages/hr/analytics";
import { HrTeamPage } from "@/pages/hr/team-mentoria";
import { HrDocumentsPage } from "@/pages/hr/documents";
import { HrAccountsPage } from "@/pages/hr/accounts";
import {
  HrAccessGrantsPage,
  HrHiringDashboardPage,
  HrLifecyclePage,
  HrPerformancePage,
  HrRecruitmentPage,
  HrSentimentPage,
} from "@/pages/hr/awaiting-backend";

import { ManagerOverviewPage } from "@/pages/manager/overview";
import { ManagerAttendancePage } from "@/pages/manager/attendance";
import { ManagerLeavePage } from "@/pages/manager/leave";
import { ManagerDocumentsPage } from "@/pages/manager/documents";

import { MyOverviewPage } from "@/pages/employee/overview";
import { MyAttendancePage } from "@/pages/employee/attendance";
import { MyLeavePage } from "@/pages/employee/leave";
import { MyDocumentsPage } from "@/pages/employee/documents";

import { OrgCalendarPage } from "@/pages/shared/org-calendar";

export function App() {
  const stage = useSession((s) => s.stage);
  const user = useSession((s) => s.user);

  /**
   * Whether a returning session is still gated lives in the signed JWT, so it
   * can only be learned by calling something the gate protects. `/api/holidays`
   * is gated by blockUntilOnboardingComplete and needed by nearly every screen
   * anyway, so this probe is shared with the real holidays query rather than
   * being an extra request.
   *
   * Deliberately NOT /api/onboarding/status: its `complete` field reports
   * whether the person has filled everything in, which is a different question
   * from whether the backend is gating them. The seeded admin account, for
   * instance, is grandfathered past the gate while having no profile on file.
   * A 403 here carries MUST_COMPLETE_ONBOARDING and the global handler in
   * main.tsx moves the session to that stage.
   */
  const gateProbe = useHolidays({ enabled: stage === "active", retry: false });

  /*
    Re-read the signed-in user from the server once the session is live, and
    write what comes back over the copy stored at login.

    Without this the local copy is frozen at sign-in: HR switching somebody to
    remote working, moving them between teams or changing their designation left
    that person's own screens describing the account as it used to be, with no
    way to notice short of logging out. React Query refetches this on mount and
    when the data goes stale, so the copy stays current for a long-lived tab.
  */
  const me = useMe(stage === "active");
  const setUser = useSession((s) => s.setUser);
  useEffect(() => {
    if (me.data?.user) setUser(me.data.user);
  }, [me.data, setUser]);

  if (stage === "anonymous" || !user) return <LoginPage />;
  if (stage === "must-reset") return <ResetPasswordPage />;
  if (stage === "onboarding") return <OnboardingPage />;
  if (gateProbe.isLoading) return <BootScreen />;

  const home = homePathFor(user.role);

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/account" element={<AccountSettingsPage />} />

        {user.role === "admin" && (
          <>
            <Route path="/hr" element={<HrOverviewPage />} />
            <Route path="/hr/org-calendar" element={<OrgCalendarPage />} />
            <Route path="/hr/attendance" element={<HrAttendancePage />} />
            <Route path="/hr/leave" element={<HrLeavePage />} />
            <Route path="/hr/analytics" element={<HrAnalyticsPage />} />
            <Route path="/hr/team" element={<HrTeamPage />} />
            <Route path="/hr/documents" element={<HrDocumentsPage />} />
            <Route path="/hr/accounts" element={<HrAccountsPage />} />
            {/* HR & Admin's own attendance and leave. Same self-service screens
                the employee role uses; every endpoint behind them is scoped to
                the caller, so no role-specific variant is needed. */}
            <Route path="/hr/my-attendance" element={<MyAttendancePage />} />
            <Route path="/hr/my-leave" element={<MyLeavePage />} />
            <Route path="/hr/my-documents" element={<MyDocumentsPage />} />
            {/* The modules with no backend behind them. nav.ts hides these from
                People-Function approver admins; the routes have to agree, or the
                nav says one thing and typing the URL says another. */}
            {!user.is_people_function_approver && (
              <>
                <Route path="/hr/performance" element={<HrPerformancePage />} />
                <Route path="/hr/lifecycle" element={<HrLifecyclePage />} />
                <Route path="/hr/recruitment" element={<HrRecruitmentPage />} />
                <Route path="/hr/hiring-dashboard" element={<HrHiringDashboardPage />} />
                <Route path="/hr/sentiment" element={<HrSentimentPage />} />
                <Route path="/hr/access" element={<HrAccessGrantsPage />} />
              </>
            )}
          </>
        )}

        {user.role === "manager" && (
          <>
            <Route path="/manager" element={<ManagerOverviewPage />} />
            <Route path="/manager/org-calendar" element={<OrgCalendarPage />} />
            <Route path="/manager/attendance" element={<ManagerAttendancePage />} />
            <Route path="/manager/leave" element={<ManagerLeavePage />} />
            <Route path="/manager/documents" element={<ManagerDocumentsPage />} />
            {/* A manager's own attendance/leave/documents. Same self-service
                screens the employee role uses; every endpoint behind them is
                scoped to the caller, so no role-specific variant is needed. */}
            <Route path="/manager/my-attendance" element={<MyAttendancePage />} />
            <Route path="/manager/my-leave" element={<MyLeavePage />} />
            <Route path="/manager/my-documents" element={<MyDocumentsPage />} />
          </>
        )}

        {user.role === "employee" && (
          <>
            <Route path="/me" element={<MyOverviewPage />} />
            <Route path="/me/org-calendar" element={<OrgCalendarPage />} />
            <Route path="/me/attendance" element={<MyAttendancePage />} />
            <Route path="/me/leave" element={<MyLeavePage />} />
            <Route path="/me/documents" element={<MyDocumentsPage />} />
          </>
        )}

        {/* Anything else lands on the role's own home rather than a dead end. */}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>
  );
}

function BootScreen() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-canvas">
      <div className="flex items-center gap-2.5 text-muted">
        <Icon name="spinner" size={20} className="animate-spin" />
        <span className="text-sm">Loading your workspace</span>
      </div>
    </div>
  );
}
